/**
 * ASCII Art Generator - Line 渲染引擎
 *
 * 使用Sobel边缘检测绘制线条艺术
 * 支持动画渲染
 */

import type { IRenderEngine, FullRenderSettings, RenderResult, StyleType } from '../types'
import type { AnimationContext } from '../animation/types'
import { applyColorMode, rgbToGrayscale } from '../algorithms/grayscale'
import {
  calculateCharacterAnimation,
  applyPulseToCharacterBrightness,
  createStaticContext,
} from './animationHelpers'

export class LineEngine implements IRenderEngine {
  readonly name: StyleType = 'line'

  /**
   * 渲染图片为线条艺术（支持动画）
   *
   * @param source 源图像
   * @param settings 渲染设置
   * @param canvas 目标 canvas
   * @param context 可选的动画上下文
   */
  render(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: FullRenderSettings,
    canvas: HTMLCanvasElement,
    context?: AnimationContext
  ): RenderResult {
    // 使用提供的动画上下文，或创建静态上下文
    const animContext = context ?? createStaticContext()

    const imageData = this.getImageData(source)
    const { width: imgWidth, height: imgHeight, data } = imageData

    const { fontSize, charSpacing, lineLength, lineThickness, lineRotation } = settings
    const cellSize = fontSize * charSpacing

    const cols = Math.max(1, Math.floor(imgWidth / cellSize))
    const rows = Math.max(1, Math.floor(imgHeight / cellSize))

    canvas.width = cols * cellSize
    canvas.height = rows * cellSize

    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = settings.backgroundColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Sobel边缘检测
    const { magnitude, direction } = this.sobelEdgeDetection(data, imgWidth, imgHeight)

    ctx.strokeStyle = this.getColor(settings)
    ctx.lineWidth = lineThickness

    const rotationRad = (lineRotation * Math.PI) / 180

    // 检查动画或鼠标交互是否启用
    const animationEnabled = settings.ditherAnimationEnabled || settings.dither2AnimationEnabled
    const mouseInteractionEnabled = settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled
    const needsAnimation = animationEnabled || mouseInteractionEnabled

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const sampleX = Math.floor(col * cellSize + cellSize / 2)
        const sampleY = Math.floor(row * cellSize + cellSize / 2)

        if (sampleX < imgWidth && sampleY < imgHeight) {
          const idx = sampleY * imgWidth + sampleX
          let mag = magnitude[idx]

          // 计算基础位置
          const baseX = col * cellSize + cellSize / 2
          const baseY = row * cellSize + cellSize / 2

          // 应用动画或鼠标交互效果
          let drawX = baseX
          let drawY = baseY

          if (needsAnimation) {
            const animResult = calculateCharacterAnimation(
              baseX, baseY, animContext, settings, cellSize, cellSize
            )

            drawX += animResult.offsetX
            drawY += animResult.offsetY

            // 应用脉动效果到线条强度（仅动画）
            if (animationEnabled) {
              mag = applyPulseToCharacterBrightness(mag, animResult.pulseFactor)
            }
          }

          // 只在边缘绘制线条
          if (mag > 50) {
            const angle = direction[idx] + rotationRad
            const len = lineLength * (mag / 255) * cellSize

            ctx.beginPath()
            ctx.moveTo(
              drawX - Math.cos(angle) * len / 2,
              drawY - Math.sin(angle) * len / 2
            )
            ctx.lineTo(
              drawX + Math.cos(angle) * len / 2,
              drawY + Math.sin(angle) * len / 2
            )
            ctx.stroke()
          }
        }
      }
    }

    return { canvas, width: canvas.width, height: canvas.height, charWidth: cellSize, charHeight: cellSize }
  }

  private sobelEdgeDetection(data: Uint8ClampedArray, width: number, height: number): { magnitude: Float32Array; direction: Float32Array } {
    const magnitude = new Float32Array(width * height)
    const direction = new Float32Array(width * height)

    // Sobel卷积核
    const Gx = [[-1, 0, 1], [-2, 0, 2], [-1, 0, 1]]
    const Gy = [[1, 2, 1], [0, 0, 0], [-1, -2, -1]]

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let sumX = 0, sumY = 0

        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * width + (x + kx)) * 4
            const gray = rgbToGrayscale(data[idx], data[idx + 1], data[idx + 2])
            sumX += gray * Gx[ky + 1][kx + 1]
            sumY += gray * Gy[ky + 1][kx + 1]
          }
        }

        const idx = y * width + x
        magnitude[idx] = Math.sqrt(sumX * sumX + sumY * sumY)
        direction[idx] = Math.atan2(sumY, sumX)
      }
    }

    return { magnitude, direction }
  }

  private getColor(settings: FullRenderSettings): string {
    const color = applyColorMode({ r: 200, g: 200, b: 200 }, settings.colorMode, settings.customColor)
    return `rgb(${color.r}, ${color.g}, ${color.b})`
  }

  getOutputSize(imageWidth: number, imageHeight: number, settings: FullRenderSettings): { width: number; height: number } {
    const cellSize = settings.fontSize * settings.charSpacing
    return { width: Math.floor(imageWidth / cellSize) * cellSize, height: Math.floor(imageHeight / cellSize) * cellSize }
  }

  private getImageData(source: HTMLImageElement | HTMLCanvasElement | ImageData): ImageData {
    if (source instanceof ImageData) return source
    const canvas = document.createElement('canvas')
    canvas.width = source.width
    canvas.height = source.height
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(source, 0, 0)
    return ctx.getImageData(0, 0, canvas.width, canvas.height)
  }
}

export const lineEngine = new LineEngine()